import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { userRequest } from "../utils/utils";
import {
  IApplicationResponse,
  ICountriesWithPosts,
  ICreateApplicationResponse,
  ICustomsListResponse,
  IDeleteFilesResponse,
  IDeleteRoutesResponse,
  IFiltrPostsRequest,
  IManagerServicesByCountry,
  IMessagesListResponse,
  IPartnerCompaniesResponse,
  IUserCommentRequest,
  IUserCommentResponse,
} from "../types";

interface IApplicationState {
  application: ICreateApplicationResponse | null;
  applicationsList: IApplicationResponse | null;
  registeredApplicationsList: IApplicationResponse | null;
  correctedApplicationsList: IApplicationResponse | null;
  paidApplicationsList: IApplicationResponse | null;
  priorityApplicationsList: IApplicationResponse | null;
  message: string;
  messagesList: IMessagesListResponse[];
  userMessage: IMessagesListResponse | null;
  customsList: ICustomsListResponse | null;
  loading: boolean;
  currentPage: number;
  currentOtherApplicationsPage: number;
  cache: Record<string, ICreateApplicationResponse>;
  lang: "en" | "ru" | "ge";
  comment: IUserCommentResponse | null;
  activeTab: string;
  searchResults: IApplicationResponse | null;
  lastSearchValue: string;
  partnersCompanies: IPartnerCompaniesResponse[] | null;
  countries: ICountriesWithPosts[] | null;
  managerServices: IManagerServicesByCountry[] | null;
  managersActiveTab: string;
}

const initialState: IApplicationState = {
  application: null,
  applicationsList: null,
  registeredApplicationsList: null,
  correctedApplicationsList: null,
  paidApplicationsList: null,
  priorityApplicationsList: null,
  message: "",
  messagesList: [],
  userMessage: null,
  customsList: null,
  loading: false,
  currentPage: 1,
  currentOtherApplicationsPage: 1,
  cache: {},
  lang: "en",
  comment: null,
  activeTab: "1",
  searchResults: null,
  lastSearchValue: "",
  partnersCompanies: null,
  countries: null,
  managerServices: null,
  managersActiveTab: "all",
};

export const createApplication = createAsyncThunk<
  ICreateApplicationResponse,
  FormData,
  { rejectValue: { error: string } }
>("applications/createApplication", async (params, { rejectWithValue }) => {
  try {
    const { data } = await userRequest.post(
      `/dashboard/api/multi-create-application/`,
      params,
    );
    return data;
  } catch (error: any) {
    console.error(error);
    return rejectWithValue({
      error: error.message || "Something went wrong",
    });
  }
});

export const getApplicationsList = createAsyncThunk<
  IApplicationResponse,
  number,
  { rejectValue: { error: string } }
>("applications/getApplicationsList", async (page, { rejectWithValue }) => {
  try {
    const { data } = await userRequest.get(
      `/dashboard/api/broker-applications/?page=${page}`,
    );
    return data;
  } catch (error: any) {
    console.error(error);
    return rejectWithValue({
      error: error.message || "Something went wrong",
    });
  }
});

export const getBrokersOthersApplicationsList = createAsyncThunk<
  IApplicationResponse,
  number,
  { rejectValue: { error: string } }
>(
  "applications/getBrokersOthersApplicationsList",
  async (page, { rejectWithValue }) => {
    try {
      const { data } = await userRequest.get(
        `/dashboard/api/broker-applications/?page=${page}`,
      );
      return data;
    } catch (error: any) {
      console.error(error);
      return rejectWithValue({
        error: error.message || "Something went wrong",
      });
    }
  },
);

export const getManagersApplicationsList = createAsyncThunk<
  IApplicationResponse,
  number,
  { rejectValue: { error: string } }
>(
  "applications/getManagersApplicationsList",
  async (page, { rejectWithValue }) => {
    try {
      const { data } = await userRequest.get(
        `/dashboard/api/manager/applications/broker-applications/?page=${page}`,
      );
      return data;
    } catch (error: any) {
      console.error(error);
      return rejectWithValue({
        error: error.message || "Something went wrong",
      });
    }
  },
);

export const getAdminApplicationsList = createAsyncThunk<
  IApplicationResponse,
  number,
  { rejectValue: { error: string } }
>(
  "applications/getAdminApplicationsList",
  async (page, { rejectWithValue }) => {
    try {
      const { data } = await userRequest.get(
        `/v5/admin/applications/?page=${page}`,
      );
      return data;
    } catch (error: any) {
      console.error(error);
      return rejectWithValue({
        error: error.message || "Something went wrong",
      });
    }
  },
);

export const getManagersOthersApplicationsList = createAsyncThunk<
  IApplicationResponse,
  number,
  { rejectValue: { error: string } }
>(
  "applications/getManagersOthersApplicationsList",
  async (page, { rejectWithValue }) => {
    try {
      const { data } = await userRequest.get(
        `/dashboard/api/manager/applications/related-applications/?page=${page}`,
      );
      return data;
    } catch (error: any) {
      console.error(error);
      return rejectWithValue({
        error: error.message || "Something went wrong",
      });
    }
  },
);

export const getBrokersApplicationsList = createAsyncThunk<
  IApplicationResponse,
  number,
  { rejectValue: { error: string } }
>(
  "applications/getBrokersApplicationsList",
  async (page, { rejectWithValue }) => {
    try {
      const { data } = await userRequest.get(
        `/dashboard/api/broker/applications/?page=${page}`,
      );
      return data;
    } catch (error: any) {
      console.error(error);
      return rejectWithValue({
        error: error.message || "Something went wrong",
      });
    }
  },
);

export const searchApplications = createAsyncThunk<
  IApplicationResponse,
  { search_term: string; page: number },
  { rejectValue: { error: string } }
>(
  "applications/searchApplications",
  async ({ search_term, page }, { rejectWithValue }) => {
    try {
      const { data } = await userRequest.post(
        `/dashboard/search/application/?page=${page}`,
        { search_term },
      );
      return data;
    } catch (error: any) {
      console.error(error);
      return rejectWithValue({
        error: error.message || "Something went wrong",
      });
    }
  },
);

export const getRegisteredApplicationsList = createAsyncThunk<
  IApplicationResponse,
  number,
  { rejectValue: { error: string } }
>(
  "applications/getRegisteredApplicationsList",
  async (page, { rejectWithValue }) => {
    try {
      const { data } = await userRequest.get(
        `/dashboard/apps/new-registered/?page=${page}`,
      );
      return data;
    } catch (error: any) {
      console.error(error);
      return rejectWithValue({
        error: error.message || "Something went wrong",
      });
    }
  },
);

export const getCorrectedApplicationsList = createAsyncThunk<
  IApplicationResponse,
  number,
  { rejectValue: { error: string } }
>(
  "applications/getCorrectedApplicationsList",
  async (page, { rejectWithValue }) => {
    try {
      const { data } = await userRequest.get(
        `/dashboard/apps/revised/?page=${page}`,
      );
      return data;
    } catch (error: any) {
      console.error(error);
      return rejectWithValue({
        error: error.message || "Something went wrong",
      });
    }
  },
);

export const getPaidApplicationsList = createAsyncThunk<
  IApplicationResponse,
  number,
  { rejectValue: { error: string } }
>("applications/getPaidApplicationsList", async (page, { rejectWithValue }) => {
  try {
    const { data } = await userRequest.get(
      `/dashboard/apps/paid-off/?page=${page}`,
    );
    return data;
  } catch (error: any) {
    console.error(error);
    return rejectWithValue({
      error: error.message || "Something went wrong",
    });
  }
});

export const getPriorityApplicationsList = createAsyncThunk<
  IApplicationResponse,
  number,
  { rejectValue: { error: string } }
>(
  "applications/getPriorityApplicationsList",
  async (page, { rejectWithValue }) => {
    try {
      const { data } = await userRequest.get(
        `/dashboard/apps/prioritized/?page=${page}`,
      );
      return data;
    } catch (error: any) {
      console.error(error);
      return rejectWithValue({
        error: error.message || "Something went wrong",
      });
    }
  },
);

export const getCustomsList = createAsyncThunk<
  ICustomsListResponse,
  number,
  { rejectValue: { error: string } }
>("applications/getCustomsList", async (id, { rejectWithValue }) => {
  try {
    const { data } = await userRequest.get(`/dashboard/list/?page=${id}`);
    return data;
  } catch (error: any) {
    console.error(error);
    return rejectWithValue({
      error: error.message || "Something went wrong",
    });
  }
});

export const getApplicationDetails = createAsyncThunk<
  ICreateApplicationResponse,
  number,
  { rejectValue: { error: string } }
>("applications/getApplicationDetails", async (id, { rejectWithValue }) => {
  try {
    const { data } = await userRequest.get(
      `dashboard/api/multi-create-application/${id}/`,
    );
    return data;
  } catch (error: any) {
    console.error(error);
    return rejectWithValue({
      error: error.message || "Something went wrong",
    });
  }
});

export const getBrokersApplicationDetails = createAsyncThunk<
  ICreateApplicationResponse,
  number,
  { rejectValue: { error: string } }
>(
  "applications/getBrokersApplicationDetails",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await userRequest.get(
        `/dashboard/api/broker/applications/${id}/`,
      );
      return data;
    } catch (error: any) {
      console.error(error);
      return rejectWithValue({
        error: error.message || "Something went wrong",
      });
    }
  },
);

export const getAdminApplicationDetails = createAsyncThunk<
  ICreateApplicationResponse,
  number,
  { rejectValue: { error: string } }
>(
  "applications/getAdminApplicationDetails",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await userRequest.get(`/v5/admin/applications/${id}/`);
      return data;
    } catch (error: any) {
      console.error(error);
      return rejectWithValue({
        error: error.message || "Something went wrong",
      });
    }
  },
);

export const getBrokerOtherApplicationDetails = createAsyncThunk<
  ICreateApplicationResponse,
  number,
  { rejectValue: { error: string } }
>(
  "applications/getBrokerOtherApplicationDetails",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await userRequest.get(
        `dashboard/api/broker-applications/${id}/`,
      );
      return data;
    } catch (error: any) {
      console.error(error);
      return rejectWithValue({
        error: error.message || "Something went wrong",
      });
    }
  },
);

export const getManagersApplicationDetails = createAsyncThunk<
  ICreateApplicationResponse,
  number,
  { rejectValue: { error: string } }
>(
  "applications/getManagersApplicationDetails",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await userRequest.get(
        `dashboard/api/manager/applications/${id}/`,
      );
      return data;
    } catch (error: any) {
      console.error(error);
      return rejectWithValue({
        error: error.message || "Something went wrong",
      });
    }
  },
);
export const updateApplication = createAsyncThunk<
  ICreateApplicationResponse,
  { id: number; formData: FormData },
  { rejectValue: { error: string } }
>(
  "applications/updateApplication",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const { data } = await userRequest.patch(
        `/dashboard/api/multi-update-application/${id}/`,
        formData,
      );
      return data;
    } catch (error) {
      console.log(error);
    }
  },
);

export const deleteFile = createAsyncThunk<
  { message: string },
  number,
  { rejectValue: { error: string } }
>("applications/deleteFile", async (id, { rejectWithValue }) => {
  try {
    const { data } = await userRequest.delete(
      `/dashboard/application-files/${id}/delete_file/`,
    );
    return data;
  } catch (err) {
    const error = err as {
      response?: {
        data?: { error?: string; non_field_errors?: string[] };
      };
    };

    const errorMessage =
      error.response?.data?.error ||
      error.response?.data?.non_field_errors?.join(", ") ||
      "An unknown error occurred";
    return rejectWithValue({ error: errorMessage });
  }
});

export const deleteApplicationRoutes = createAsyncThunk<
  IDeleteRoutesResponse,
  { id: number; route_ids: number[] },
  { rejectValue: { error: string } }
>(
  "applications/deleteApplicationRoutes",
  async ({ id, route_ids }, { rejectWithValue }) => {
    try {
      const { data } = await userRequest.delete(
        `/dashboard/api/delete-routes/${id}/`,
        { data: { route_ids } },
      );
      return data;
    } catch (err) {
      const error = err as {
        response?: {
          data?: { error?: string; non_field_errors?: string[] };
        };
      };

      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.non_field_errors?.join(", ") ||
        "An unknown error occurred";
      return rejectWithValue({ error: errorMessage });
    }
  },
);

export const deleteApplicationFiles = createAsyncThunk<
  IDeleteFilesResponse,
  { id: number; file_ids: number[] },
  { rejectValue: { error: string } }
>(
  "applications/deleteApplicationFiles",
  async ({ id, file_ids }, { rejectWithValue }) => {
    try {
      const { data } = await userRequest.delete(
        `/dashboard/api/delete-files/${id}/`,
        { data: { file_ids } },
      );
      return data;
    } catch (err) {
      const error = err as {
        response?: {
          data?: { error?: string; non_field_errors?: string[] };
        };
      };

      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.non_field_errors?.join(", ") ||
        "An unknown error occurred";
      return rejectWithValue({ error: errorMessage });
    }
  },
);

export const changeStatus = createAsyncThunk<
  ICreateApplicationResponse,
  {
    id: number;
    params:
      | {
          id: number;
          services: [
            {
              id: number;
              status: string;
              comment?: string;
            },
          ];
        }
      | FormData;
  },
  { rejectValue: { error: string } }
>("applications/changeStatus", async ({ id, params }, { rejectWithValue }) => {
  try {
    const { data } = await userRequest.patch(
      `/dashboard/api/broker-update-application/${id}/`,
      params,
    );

    return data;
  } catch (error) {
    const err = error as { response?: { data?: { error?: string } } };
    return rejectWithValue({
      error: err.response?.data?.error || "An unknown error occurred",
    });
  }
});

export const getMessagesList = createAsyncThunk<
  IMessagesListResponse[],
  number,
  { rejectValue: { error: string } }
>("messages/getMessagesList", async (id, { rejectWithValue }) => {
  try {
    const { data } = await userRequest.get(
      `/dashboard/messages/application/${id}/`,
    );
    return data;
  } catch (error: any) {
    console.error(error);
    return rejectWithValue({
      error: error.message || "Something went wrong",
    });
  }
});

export const addMessage = createAsyncThunk<
  IMessagesListResponse,
  {
    application: number;
    content: string | JSX.Element;
    application_status: string;
  },
  { rejectValue: { error: string } }
>("messages/addMessage", async (params, { rejectWithValue }) => {
  try {
    const { data } = await userRequest.post(`/dashboard/messages/`, params);
    return data;
  } catch (error: any) {
    console.error(error);
    return rejectWithValue({
      error: error.message || "Something went wrong",
    });
  }
});

export const createUserComment = createAsyncThunk<
  IUserCommentResponse,
  IUserCommentRequest,
  { rejectValue: { error: string } }
>("application/addUserComment", async (params, { rejectWithValue }) => {
  try {
    const { data } = await userRequest.post(
      `/dashboard/user-comments/`,
      params,
    );
    return data;
  } catch (error: any) {
    console.error(error);
    return rejectWithValue({
      error: error.message || "Something went wrong",
    });
  }
});

export const filtrCustomsPosts = createAsyncThunk<
  ICustomsListResponse,
  IFiltrPostsRequest,
  { rejectValue: { error: string } }
>("applications/filtrCustomsList", async (params, { rejectWithValue }) => {
  try {
    const { data } = await userRequest.post(
      `/dashboard/office-search/`,
      params,
    );
    return data;
  } catch (error: any) {
    console.error(error);
    return rejectWithValue({
      error: error.message || "Something went wrong",
    });
  }
});

export const getPartnerCompanies = createAsyncThunk<
  IPartnerCompaniesResponse[],
  void,
  { rejectValue: { error: string } }
>("applications/getPartnerCompanies", async (id, { rejectWithValue }) => {
  try {
    const { data } = await userRequest.get(`/dashboard/partner-company/`);
    return data;
  } catch (error: any) {
    console.error(error);
    return rejectWithValue({
      error: error.message || "Something went wrong",
    });
  }
});

export const getCountriesWithPosts = createAsyncThunk<
  ICountriesWithPosts[],
  void,
  { rejectValue: { error: string } }
>("applications/getContriesWithPosts", async (_, { rejectWithValue }) => {
  try {
    const { data } = await userRequest.get(`/dashboard/api/transit-structure/`);
    return data;
  } catch (error: any) {
    console.error(error);
    return rejectWithValue({
      error: error.message || "Something went wrong",
    });
  }
});

export const getManagerServicesByCountry = createAsyncThunk<
  IManagerServicesByCountry[],
  void,
  { rejectValue: { error: string } }
>(
  "applications/getManagerServicesByCountry",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await userRequest.get(
        `/dashboard/api/services-by-country/`,
      );
      return data;
    } catch (error: any) {
      console.error(error);
      return rejectWithValue({
        error: error.message || "Something went wrong",
      });
    }
  },
);

export const filtrApplicationsByStatusesAndEmployees = createAsyncThunk<
  IApplicationResponse,
  { page: number; status: string; user_id?: number },
  { rejectValue: { error: string } }
>(
  "applications/filtrApplicationsByStatusesAndEmployees",
  async (params, { rejectWithValue }) => {
    try {
      const { data } = await userRequest.post(
        `/dashboard/api/manager/applications/by-user-status/?page=${params.page}`,
        params,
      );
      return data;
    } catch (error: any) {
      console.error(error);
      return rejectWithValue({
        error: error.message || "Something went wrong",
      });
    }
  },
);

export const searchManagersApplications = createAsyncThunk<
  IApplicationResponse,
  { search_term: string; page: number },
  { rejectValue: { error: string } }
>(
  "applications/searchManagersApplications",
  async ({ search_term, page }, { rejectWithValue }) => {
    try {
      const { data } = await userRequest.post(
        `/dashboard/search/manager/?page=${page}`,
        { search_term },
      );
      return data;
    } catch (error: any) {
      console.error(error);
      return rejectWithValue({
        error: error.message || "Something went wrong",
      });
    }
  },
);

export const applicationSlice = createSlice({
  name: "application",
  initialState,
  reducers: {
    setCurrentPageNumber: (state, action) => {
      state.currentPage = action.payload;
    },
    setCurrentOtherApplicationsPageNumber: (state, action) => {
      state.currentOtherApplicationsPage = action.payload;
    },

    setLang(state, action) {
      state.lang = action.payload;
    },
    setActiveTab: (state, action) => {
      state.activeTab = action.payload;
    },
    setLastSearchValue: (state, action) => {
      state.lastSearchValue = action.payload;
    },
    resetApplication: () => initialState,
    setManagersActiveTab: (state, action) => {
      state.managersActiveTab = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createApplication.fulfilled, (state, action) => {
        state.application = action.payload;
        state.loading = false;
      })
      .addCase(createApplication.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(createApplication.rejected, (state, action) => {
        state.loading = false;
      })

      .addCase(getApplicationsList.fulfilled, (state, action) => {
        state.applicationsList = action.payload;
        state.loading = false;
      })
      .addCase(getBrokersApplicationsList.fulfilled, (state, action) => {
        state.applicationsList = action.payload;
        state.loading = false;
      })
      .addCase(getManagersApplicationsList.fulfilled, (state, action) => {
        state.applicationsList = action.payload;
        state.loading = false;
      })
      .addCase(getManagersOthersApplicationsList.fulfilled, (state, action) => {
        state.applicationsList = action.payload;
        state.loading = false;
      })
      .addCase(getBrokersOthersApplicationsList.fulfilled, (state, action) => {
        state.applicationsList = action.payload;
        state.loading = false;
      })
      .addCase(getApplicationsList.pending, (state, action) => {
        state.loading = true;
      })

      .addCase(searchApplications.fulfilled, (state, action) => {
        state.searchResults = action.payload;
      })

      .addCase(getRegisteredApplicationsList.fulfilled, (state, action) => {
        state.registeredApplicationsList = action.payload;
        state.loading = false;
      })
      .addCase(getRegisteredApplicationsList.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(getRegisteredApplicationsList.rejected, (state, action) => {
        state.loading = false;
      })

      .addCase(getCorrectedApplicationsList.fulfilled, (state, action) => {
        state.correctedApplicationsList = action.payload;
        state.loading = false;
      })
      .addCase(getCorrectedApplicationsList.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(getCorrectedApplicationsList.rejected, (state, action) => {
        state.loading = false;
      })

      .addCase(getPaidApplicationsList.fulfilled, (state, action) => {
        state.paidApplicationsList = action.payload;
        state.loading = false;
      })
      .addCase(getPaidApplicationsList.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(getPaidApplicationsList.rejected, (state, action) => {
        state.loading = false;
      })

      .addCase(getPriorityApplicationsList.fulfilled, (state, action) => {
        state.priorityApplicationsList = action.payload;
        state.loading = false;
      })
      .addCase(getPriorityApplicationsList.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(getPriorityApplicationsList.rejected, (state, action) => {
        state.loading = false;
      })

      .addCase(getApplicationDetails.fulfilled, (state, action) => {
        state.application = action.payload;
        state.cache = state.cache || {};
        state.cache[action.payload.id] = action.payload;
        state.loading = false;
      })

      .addCase(getBrokersApplicationDetails.fulfilled, (state, action) => {
        state.application = action.payload;
        state.cache = state.cache || {};
        state.cache[action.payload.id] = action.payload;
        state.loading = false;
      })
      .addCase(getBrokerOtherApplicationDetails.fulfilled, (state, action) => {
        state.application = action.payload;
        state.cache = state.cache || {};
        state.cache[action.payload.id] = action.payload;
        state.loading = false;
      })

      .addCase(getAdminApplicationDetails.fulfilled, (state, action) => {
        state.application = action.payload;
        state.cache = state.cache || {};
        state.cache[action.payload.id] = action.payload;
        state.loading = false;
      })

      .addCase(getManagersApplicationDetails.fulfilled, (state, action) => {
        state.application = action.payload;
      })
      .addCase(getApplicationDetails.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(getApplicationDetails.rejected, (state, action) => {
        state.loading = false;
      })

      // .addCase(updateApplication.fulfilled, (state, action) => {
      //   state.application = action.payload;
      //   state.loading = false;
      // })
      .addCase(updateApplication.pending, (state, action) => {
        state.loading = true;
      })

      .addCase(updateApplication.rejected, (state, action) => {
        state.loading = false;
      })

      .addCase(deleteFile.fulfilled, (state, action) => {
        state.message = action.payload?.message;
      })
      .addCase(getMessagesList.fulfilled, (state, action) => {
        state.messagesList = action.payload;
      })

      .addCase(addMessage.fulfilled, (state, action) => {
        state.userMessage = action.payload;
      })
      .addCase(getCustomsList.fulfilled, (state, action) => {
        state.customsList = action.payload;
      })
      .addCase(createUserComment.fulfilled, (state, action) => {
        state.comment = action.payload;
      })
      .addCase(getPartnerCompanies.fulfilled, (state, action) => {
        state.partnersCompanies = action.payload;
      })
      .addCase(getCountriesWithPosts.fulfilled, (state, action) => {
        state.countries = action.payload;
      })
      .addCase(getManagerServicesByCountry.fulfilled, (state, action) => {
        state.managerServices = action.payload;
      })
      .addCase(
        filtrApplicationsByStatusesAndEmployees.fulfilled,
        (state, action) => {
          state.applicationsList = action.payload;
        },
      )
      .addCase(searchManagersApplications.fulfilled, (state, action) => {
        state.searchResults = action.payload;
      });
  },
});
export const {
  setCurrentPageNumber,
  setCurrentOtherApplicationsPageNumber,
  setLang,
  setActiveTab,
  setLastSearchValue,
  resetApplication,
  setManagersActiveTab,
} = applicationSlice.actions;
export default applicationSlice.reducer;
